import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Modal from '../components/Modal';
import { Plus, Edit3, Trash2, Printer } from 'lucide-react';

const PRESETS = [
  { name: 'X1 Carbon', brand: 'Bambu Lab', build_width: 256, build_depth: 256, build_height: 256 },
  { name: 'P1S', brand: 'Bambu Lab', build_width: 256, build_depth: 256, build_height: 256 },
  { name: 'A1', brand: 'Bambu Lab', build_width: 256, build_depth: 256, build_height: 256 },
  { name: 'A1 Mini', brand: 'Bambu Lab', build_width: 180, build_depth: 180, build_height: 180 },
  { name: 'X2D', brand: 'Bambu Lab', build_width: 256, build_depth: 256, build_height: 256 },
  { name: 'P2S', brand: 'Bambu Lab', build_width: 256, build_depth: 256, build_height: 256 },
  { name: 'MK4S', brand: 'Prusa', build_width: 250, build_depth: 210, build_height: 220 },
  { name: 'Mini+', brand: 'Prusa', build_width: 180, build_depth: 180, build_height: 180 },
  { name: 'XL', brand: 'Prusa', build_width: 360, build_depth: 360, build_height: 360 },
  { name: 'Centauri Carbon', brand: 'Elegoo', build_width: 220, build_depth: 220, build_height: 265 },
];

export default function MachinesPage() {
  const [machines, setMachines] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [form, setForm] = useState({ name: '', brand: '', model: '', build_width: 256, build_depth: 256, build_height: 256, notes: '' });

  const loadMachines = () => api.get('/machines').then(r => setMachines(r.data.machines));
  useEffect(() => { loadMachines(); }, []);

  const handleCreate = async () => {
    if (!form.name) return;
    await api.post('/machines', form);
    setShowCreate(false);
    setForm({ name: '', brand: '', model: '', build_width: 256, build_depth: 256, build_height: 256, notes: '' });
    loadMachines();
  };

  const handleUpdate = async () => {
    await api.put(`/machines/${showEdit}`, form);
    setShowEdit(null);
    loadMachines();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this machine? G-code profiles linked to it will also be removed.')) return;
    await api.delete(`/machines/${id}`);
    loadMachines();
  };

  const applyPreset = (preset) => {
    setForm({ ...form, ...preset });
  };

  const openEdit = (machine) => {
    setForm({ name: machine.name, brand: machine.brand, model: machine.model, build_width: machine.build_width, build_depth: machine.build_depth, build_height: machine.build_height, notes: machine.notes || '' });
    setShowEdit(machine.id);
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>Machines</h1>
        <button className="btn btn-primary" onClick={() => { setForm({ name: '', brand: '', model: '', build_width: 256, build_depth: 256, build_height: 256, notes: '' }); setShowCreate(true); }}>
          <Plus size={16} /> Add Machine
        </button>
      </div>

      {machines.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px', color: 'var(--text-muted)' }}>
          <Printer size={56} style={{ marginBottom: '16px', opacity: 0.2 }} />
          <p style={{ fontSize: '18px', marginBottom: '8px' }}>No machines yet</p>
          <p style={{ fontSize: '14px' }}>Add your 3D printers to manage G-code profiles</p>
        </div>
      ) : (
        <div className="grid grid-3">
          {machines.map(m => (
            <div key={m.id} className="card">
              <div className="card-body">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{m.brand}</div>
                    <div style={{ fontSize: '18px', fontWeight: '600' }}>{m.name}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button className="btn-icon" style={{ padding: '6px' }} onClick={() => openEdit(m)}><Edit3 size={14} /></button>
                    <button className="btn-icon" style={{ padding: '6px' }} onClick={() => handleDelete(m.id)}><Trash2 size={14} /></button>
                  </div>
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  Build volume: {m.build_width} × {m.build_depth} × {m.build_height} mm
                </div>
                {m.notes && <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '6px' }}>{m.notes}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showCreate || showEdit !== null}
        onClose={() => { setShowCreate(false); setShowEdit(null); }}
        title={showEdit ? 'Edit Machine' : 'Add Machine'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => { setShowCreate(false); setShowEdit(null); }}>Cancel</button>
            <button className="btn btn-primary" onClick={showEdit ? handleUpdate : handleCreate}>
              {showEdit ? 'Save' : 'Add Machine'}
            </button>
          </>
        }
      >
        {showCreate && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>Quick presets:</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {PRESETS.map((p, i) => (
                <button key={i} className="btn btn-secondary btn-sm" onClick={() => applyPreset(p)}>
                  {p.brand} {p.name}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="form-group">
          <label>Name</label>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. X1 Carbon" />
        </div>
        <div className="form-group">
          <label>Brand</label>
          <input value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} placeholder="e.g. Bambu Lab" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
          <div className="form-group">
            <label>Width (mm)</label>
            <input type="number" value={form.build_width} onChange={e => setForm({ ...form, build_width: parseFloat(e.target.value) })} />
          </div>
          <div className="form-group">
            <label>Depth (mm)</label>
            <input type="number" value={form.build_depth} onChange={e => setForm({ ...form, build_depth: parseFloat(e.target.value) })} />
          </div>
          <div className="form-group">
            <label>Height (mm)</label>
            <input type="number" value={form.build_height} onChange={e => setForm({ ...form, build_height: parseFloat(e.target.value) })} />
          </div>
        </div>
        <div className="form-group">
          <label>Notes</label>
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Optional notes..." />
        </div>
      </Modal>
    </div>
  );
}
